import jwtDecode from 'jwt-decode';
import { isNumber, min, omitBy, round } from 'lodash';
import { DateTime } from 'luxon';
import { GwscoutJwtPayload, useAuthenticationStore } from 'src/stores/Authentication';

import { ConvertOverload } from '../hooks/useCurrency';
import UserRole from '../types/UserRoles';

import { isUcs } from './car-source-helper';
import { makeMonetaryAmount, SupportedCurrencyCode } from './currency';
import {
  BidDTO,
  BidSuccessStatus,
  BidType,
  CountryCode,
  CurrencyCode,
  DealerId,
  DetailCarDTO,
  DetailCarPreviousAuctionDTO,
  IndividualBidDTO,
  KvpsAndName,
  MonetaryAmount,
  OfferType,
  SourceType,
  TransportationProvider,
  TransportOptionDTO,
} from './generated/api';
import { PROVIDERS_SHOWN_AS_VIA_ALLMOBIL, transportOptionLocalStorage } from './global-vars';
import { discountBid } from './math-helpers';
import { getAccountValuationCountry } from './valuation-country-helpers';

export const getMinOfPrevAuctions = (previousAuctions: DetailCarPreviousAuctionDTO[], packageId?: string) => {
  if (packageId) {
    return min(
      previousAuctions
        .filter(
          (auction) =>
            auction.offerType === OfferType.Package &&
            (auction.priceOrientation !== undefined || auction.priceFixed !== undefined),
        )
        .map((auction) => auction.priceOrientation?.amount || auction.priceFixed?.amount || 0),
    );
  }
  return min(
    previousAuctions
      .filter(
        (auction) =>
          auction.offerType === OfferType.Individual &&
          (auction.priceOrientation !== undefined || auction.priceFixed !== undefined),
      )
      .map((auction) => auction.priceOrientation?.amount || auction.priceFixed?.amount || 0),
  );
};
export const bidTimeInPast = (biddingTime: string | undefined) => {
  if (biddingTime) {
    return new Date(biddingTime).getTime() < Date.now();
  }
  return true;
};

export const getBidAlertTitle = (successStatus: BidSuccessStatus | undefined, bidEditMode: boolean) => {
  switch (successStatus) {
    case BidSuccessStatus.Sent:
      return 'bids.hintSent';
    case BidSuccessStatus.Scheduled:
      return bidEditMode ? 'bids.hintOverwrite' : 'bids.hintScheduled';
    case BidSuccessStatus.Bought:
      return 'bids.hintBought';
    default:
      return '';
  }
};

export const getTimerValue = (dateEnd: string | undefined, dateBuyOpening: string | undefined) => {
  const parsedDateEnd = dateEnd && new Date(dateEnd);
  const parsedDateBuyOpening = dateBuyOpening && new Date(dateBuyOpening);
  const currentDate = new Date();
  const isOpen =
    parsedDateBuyOpening &&
    parsedDateBuyOpening.getTime() &&
    parsedDateBuyOpening.getTime() - currentDate.getTime() <= 0;
  const isEndingSoon =
    parsedDateEnd && parsedDateEnd.getTime() && parsedDateEnd.getTime() - currentDate.getTime() <= 1000 * 60 * 60 * 12;

  switch (true) {
    case isOpen === false:
      return [dateBuyOpening!, 'until']; // Auction starts in...
    case isEndingSoon === true:
      return [dateEnd!, 'down']; // Auction ends in...
    case isOpen === true:
    case isEndingSoon === false:
      return [dateBuyOpening!, 'up']; // Auction is running for...
    default:
      return false;
  }
};
export const SOURCES_BID_ALLOWED: SourceType[] = [SourceType.Vwfs, SourceType.Ucs, SourceType.SpotdealsAllmobil];

const NON_VALID_BID_SUCCESS_STATES = new Set([
  BidSuccessStatus.Error,
  BidSuccessStatus.Cancelled,
  BidSuccessStatus.Deleted,
  BidSuccessStatus.Outbid,
]);

/**
 * True while a failed fixed bid document still holds a retry entry the backend may send.
 *
 * Only meaningful for a document whose successStatus is ERROR. The backend derives that document
 * status from its own bids by priority (BOUGHT > ALLOCATE > HIGHEST > OUTBID > SENT > ERROR >
 * NOT_SENT > CANCELLED > DELETED), so any entry ranking above ERROR would have become the document
 * status itself. Inside an ERROR document a SENT entry is therefore always the NOT_SENT entry that
 * non-admins receive masked as SENT, and an OUTBID entry is a masked CANCELLED - both are final.
 * SCHEDULED is absent from that priority list and loses against ERROR, which makes it the only
 * status (besides a not yet assigned one) that really means "retry still on its way".
 */
const hasScheduledFixedBidRetry = (bid?: BidDTO): boolean =>
  !!bid?.bids?.some(({ status }: IndividualBidDTO) => !status || status === BidSuccessStatus.Scheduled);

export const getBiddingStatus = (bid?: BidDTO): BidSuccessStatus | undefined => {
  const lastBidFixed = bid?.bids?.[0]?.bidType === BidType.Fixed;
  if (lastBidFixed) {
    const { successStatus } = bid;
    // states that indicate that the bid was not successful
    if (!successStatus || NON_VALID_BID_SUCCESS_STATES.has(successStatus)) {
      // a retry that the backend may still act on locks the form, treat it as Bought
      // temporary state until the BE updates successStatus from ERROR to SENT
      if (successStatus === BidSuccessStatus.Error && hasScheduledFixedBidRetry(bid)) {
        return BidSuccessStatus.Bought;
      }
      return successStatus;
    }
    // for states like scheduled, sent, etc we need to return Bought because fixed bids can't be changed after sending
    return BidSuccessStatus.Bought;
  }

  // indicate the user that the bid was sent even if that is not yet true in our system
  if (bid?.successStatus === BidSuccessStatus.Scheduled) {
    return bidTimeInPast(bid.biddingTime) ? BidSuccessStatus.Sent : BidSuccessStatus.Scheduled;
  }
  return bid?.successStatus;
};

export const useAccountDealerId = (): string[] | undefined => {
  const {
    state: { accessToken },
  } = useAuthenticationStore();
  if (accessToken) {
    const token = jwtDecode<GwscoutJwtPayload>(accessToken);
    return token.dealer_id;
  }
  return undefined;
};

export const wasAuctionAlreadyVisible = (visibleForKvps: string[] | undefined) => !!visibleForKvps?.length;

type BidDefaultProps = {
  car: DetailCarDTO;
  convert: ConvertOverload;
  hasRole: (role: UserRole) => boolean;
};

export const getBidDefaults = ({
  car: { previousAuctions, packageId, bid, bidStateInfo, source },
  hasRole,
  convert,
}: BidDefaultProps) => {
  const actualBidSuccessState = getBiddingStatus(bid);

  const colleagueBidActive = bidStateInfo?.highestHistoricBid?.amount;
  const base = getMinOfPrevAuctions(previousAuctions || [], packageId) || 0;

  const bids = bid?.bids;
  type BidsType = {
    bid1: Required<MonetaryAmount> | undefined;
    bid2: Required<MonetaryAmount> | undefined;
    bid3: Required<MonetaryAmount> | undefined;
  };

  const overbidColleague: BidsType = {
    bid1: colleagueBidActive
      ? { amount: colleagueBidActive + 100, currency: bidStateInfo.highestHistoricBid?.currency || 'EUR' }
      : undefined,
    bid2: colleagueBidActive
      ? { amount: colleagueBidActive + 200, currency: bidStateInfo.highestHistoricBid?.currency || 'EUR' }
      : undefined,
    bid3: colleagueBidActive
      ? { amount: colleagueBidActive + 300, currency: bidStateInfo.highestHistoricBid?.currency || 'EUR' }
      : undefined,
  };

  const prevBid: BidsType = {
    bid1: isNumber(bids?.[0]?.bid?.value)
      ? convert(makeMonetaryAmount(bids![0]!.bid!.value, bids![0]!.bid!.unit as SupportedCurrencyCode), {})
      : undefined,
    bid2: isNumber(bids?.[1]?.bid?.value)
      ? convert(makeMonetaryAmount(bids![1]!.bid!.value, bids![1]!.bid!.unit as SupportedCurrencyCode), {})
      : undefined,
    bid3: isNumber(bids?.[2]?.bid?.value)
      ? convert(makeMonetaryAmount(bids![2]!.bid!.value, bids![2]!.bid!.unit as SupportedCurrencyCode), {})
      : undefined,
  };
  const baseBid: BidsType = {
    // base minus 5/4/3 percent
    bid1: convert(makeMonetaryAmount(discountBid(base, 0.05), CurrencyCode.Eur), {}),
    bid2: convert(makeMonetaryAmount(discountBid(base, 0.04), CurrencyCode.Eur), {}),
    bid3: convert(makeMonetaryAmount(discountBid(base, 0.03), CurrencyCode.Eur), {}),
  };

  const defaultBiddingTime =
    actualBidSuccessState === undefined || actualBidSuccessState === BidSuccessStatus.Scheduled ? '60' : '0';

  return {
    biddingTime: defaultBiddingTime,
    ...(hasRole(UserRole.DEALER) && prevBid),
    ...(hasRole(UserRole.ADMIN) &&
      !bids && {
        bid1: overbidColleague.bid1 || prevBid.bid1 || baseBid.bid1,
        bid2: source && !isUcs(source) ? overbidColleague.bid2 || prevBid.bid2 || baseBid.bid2 : undefined,
        bid3: source && !isUcs(source) ? overbidColleague.bid3 || prevBid.bid3 || baseBid.bid3 : undefined,
      }),
  };
};
export type BidBuyErrors = {
  colleagueBidActive?: boolean;
  credentialsMissing?: boolean;
  illegalAccountValuationCountry?: boolean;
  bidMustBeSmallerThanBuyPrice?: boolean;
};

export const isBuyingAllowedForUCS = (dateStart?: string, dateBuyOpening?: string): boolean => {
  // For UCS buying not allowed if the start time > 720 hours away
  const startDate = DateTime.fromISO(dateBuyOpening || dateStart!);
  const today = DateTime.now();

  const hoursTillStart = round(startDate.diff(today, 'hours').hours, 0);
  if (hoursTillStart <= 720) return true;
  return false;
};

export const transformDealerIdToDealerIdDTO = (dealerId?: string): DealerId | undefined => {
  if (dealerId && dealerId.length > 0) {
    return { id: dealerId, country: dealerId.substring(0, 2) as CountryCode };
  }
  return undefined;
};

export const selectDefaultTransportOption = (
  transportOptions: TransportOptionDTO[],
  ownFirstCall: boolean = false,
): string | undefined => {
  // (1) if only one option, select this one
  if (transportOptions.length === 1) {
    if (transportOptions[0].provider === TransportationProvider.PlatformDefault) {
      // Workaround disable Default for now IRIS-4837
      return undefined;
    }
    return transportOptions[0].id;
  }

  // (2) if own firstCall select VwfsFirstCallPickup - if available
  if (ownFirstCall) {
    const pickupOption = transportOptions.find(
      (option) => option.provider === TransportationProvider.VwfsFirstCallPickup,
    );

    if (pickupOption) {
      return pickupOption.id;
    }
  }

  // (3) use last selected from local storage
  // (4) if no last option or not available - no default select
  let transportLocalStorage = (localStorage.getItem(transportOptionLocalStorage) || '') as TransportationProvider;
  if (!transportLocalStorage) return '';
  if (!isValidTransportationProvider(transportLocalStorage)) {
    // Handle invalid value - maybe clear it and return default
    localStorage.removeItem(transportOptionLocalStorage);
    return '';
  }

  transportLocalStorage = transportLocalStorage as TransportationProvider;

  if (transportLocalStorage === TransportationProvider.PlatformDefault) {
    return ''; // Workaround disable Default for now IRIS-4837
  }

  // First, try to find an exact match
  const exactMatch = transportOptions.find((option) => option.provider === transportLocalStorage);
  if (exactMatch) {
    return exactMatch.id;
  }

  // If no exact match and the provider is in PROVIDERS_SHOWN_AS_VIA_ALLMOBIL,
  // find the first matching provider from that list
  if (PROVIDERS_SHOWN_AS_VIA_ALLMOBIL.includes(transportLocalStorage)) {
    const fallbackMatch = transportOptions.find((option) =>
      PROVIDERS_SHOWN_AS_VIA_ALLMOBIL.includes(option.provider as TransportationProvider),
    );
    if (fallbackMatch) {
      return fallbackMatch.id;
    }
  }

  // Handle pickup providers (VwfsFirstCallPickup/VwfsPickup) as Fallback
  if ([TransportationProvider.VwfsFirstCallPickup, TransportationProvider.VwfsPickup].includes(transportLocalStorage)) {
    const pickupMatch = transportOptions.find(
      (option) =>
        option.provider === TransportationProvider.VwfsPickup ||
        option.provider === TransportationProvider.VwfsFirstCallPickup,
    );
    return pickupMatch?.id;
  }

  // eslint-disable-next-line no-console
  console.error(`Unhandled TransportationProvider: ${transportLocalStorage}`);
  return '';

  // Check if string is part of the TransportationProvider enum
  function isValidTransportationProvider(value: string): value is TransportationProvider {
    return Object.values(TransportationProvider).includes(value as TransportationProvider);
  }
};

export const allowedValuationCountries = [
  'AT',
  'FR',
  'DE',
  'IT',
  'ES',
  'HU',
  'PL',
  'RO',
  'CZ',
  'SI',
  'HR',
  'SK',
  'PT',
  'SE',
  'BG',
  'AL',
  'MK',
  'XK',
  'NL',
  'BE',
  'NO',
  'DK',
  'CH',
];

export const isFixedBidRetryable = (bid: BidDTO | undefined, dateEnd: string | undefined): boolean =>
  bid?.bids?.[0]?.bidType === BidType.Fixed &&
  bid.successStatus === BidSuccessStatus.Error &&
  !hasScheduledFixedBidRetry(bid) &&
  !!dateEnd &&
  new Date(dateEnd) > new Date();

export const getBidBuyErrors = ({ isNoKVPSCar, kvps }: { isNoKVPSCar: boolean; kvps?: KvpsAndName[] }): BidBuyErrors =>
  omitBy(
    {
      credentialsMissing: !isNoKVPSCar && !kvps?.length,
      illegalAccountValuationCountry: !allowedValuationCountries.includes(getAccountValuationCountry(true)),
    },
    (pred) => !pred,
  ) as BidBuyErrors;
